import {
  adt,
  constant,
  constFalse,
  constTrue,
  either,
  flow,
  fromThrown,
  iots,
  pipe,
} from '@code-expert/prelude';

export const BooleanFromString: iots.BooleanFromStringC = new iots.Type<boolean, string, unknown>(
  'BooleanFromString',
  iots.boolean.is,
  (u, c) =>
    pipe(
      iots.keyof({ true: null, 1: null }).validate(u, c),
      either.as(true),
      either.alt(() => pipe(iots.keyof({ false: null, 0: null }).validate(u, c), either.as(false))),
    ),
  String,
);

const zeroOrOneKeys = { 0: null, 1: null };
const foldZeroOrOne = adt.foldFromKeys(zeroOrOneKeys);

export const BooleanFromZeroOrOneString: iots.BooleanFromStringC = new iots.Type<
  boolean,
  string,
  unknown
>(
  'BooleanFromZeroOrOneString',
  iots.boolean.is,
  (u, c) =>
    pipe(
      iots.keyof({ 0: null, 1: null }).validate(u, c),
      either.map(foldZeroOrOne({ '0': constFalse, '1': constTrue })),
    ),
  (b) => (b ? '1' : '0'),
);

/**
 * Encodes `true` as `'1'`. Useful for partial codecs where properties with the value `false` should
 * be omitted, e.g. for request parameters.
 */
export const TrueFromOne: iots.Type<true, '1', unknown> = new iots.Type(
  'TrueFromOne',
  (u: unknown): u is true => u === true,
  (u, context) => pipe(iots.literal('1').validate(u, context), either.as(true)),
  constant('1'),
);

export const Json = <A>(codec: iots.Type<A, unknown>): iots.Type<A, string> =>
  new iots.Type(
    'Json',
    codec.is,
    (u, context) =>
      pipe(
        iots.string.validate(u, context),
        either.chainW((value) =>
          either.tryCatch(
            () => JSON.parse(value),
            flow(fromThrown, ({ message }) => [{ value, context, message }]),
          ),
        ),
      ),
    flow(codec.encode, JSON.stringify),
  );
